<template>
  <div
    class="vx-card contact-list-toolbar text-content px-2"
    :class="[selectedSegmentLocal ? 'bg-active' : 'bg-theme-background',
      (someContactMatchOnSegmentOrFilter || selectedFromCampaign) ? 'contact-list-toolbar--with-info' : '']">
    <!-- 4-column, mobile-first layout -->
    <div class="contact-list-toolbar-grid">
      <!-- Col 1: Segment selector -->
      <div class="ctl-col ctl-col--segment box">
        <div class="ctl-segment-inner">
          <div class="view-segment-button ctl-segment-select border-0 border-r border-solid border-grey-light">
            <v-select-server
              ref="vSelectServer"
              v-model="selectedSegmentLocal"
              class="contact-list-toolbar-select"
              :fetch-function="fetchAllSegments"
              :placeholder="selectSegmentPlaceholder"
              close-on-select
              manage-route-name="segments"
              :manage-text="$t('$ContactModule.ManageSegments')"
              @create="(name) => $emit('new-segment', name)"/>
          </div>

          <div
            v-if="!selectedSegmentLocal"
            class="new-segment-button ctl-segment-new border-0 border-r border-solid border-grey-light">
            <a
              href="#"
              class="whitespace-no-wrap text-content"
              @click.prevent="$emit('new-segment')">
              {{ $t('$ContactModule.NewSegment') }}
            </a>
          </div>
        </div>
      </div>

      <!-- Col 2: Count -->
      <div
        v-if="someSegmentOrFilter || selectedFromCampaign"
        class="ctl-col ctl-col--count">
        <span class="text-content ctl-count-text" v-if="someSegmentOrFilter">
          {{ contactsInfoMsg }}
        </span>
        <strong class="text-content ctl-count-text" v-else>
          {{ sendToContactsMsg }}
        </strong>
      </div>

      <!-- Col 3: Actions -->
      <div
        v-if="selectedSegmentLocal && someContactMatchOnSegmentOrFilter"
        class="ctl-col ctl-col--actions ctl-sep-left">
        <vx-tooltip
          text="Edit segment"
          position="top"
          class="inline-block">
          <a class="flex items-center text-content cursor-pointer mr-3" href.prevent @click="$emit('edit-segment')">
            <feather-icon
              icon="Edit2Icon"
              svgClasses="h-5 w-5"/>
          </a>
        </vx-tooltip>

        <vx-tooltip
          v-if="!selectedFromCampaign"
          text="Send campaign"
          position="top"
          class="inline-block">
          <a class="flex items-center text-content cursor-pointer" href.prevent @click="sendCampaign()">
            <feather-icon
              icon="SendIcon"
              svgClasses="h-5 w-5"/>
          </a>
        </vx-tooltip>
      </div>

      <!-- Col 4: More -->
      <div class="ctl-col ctl-col--more ctl-sep-left">
        <vx-tooltip
          :text="$t('$General.More')"
          position="top"
          class="inline-block">
          <vs-dropdown
            class="cursor-pointer"
            vs-trigger-click>
            <a class="flex items-center text-content" href.prevent>
              <feather-icon icon="MoreVerticalIcon" svgClasses="h-7 w-7" />
            </a>

            <vs-dropdown-menu>
              <vs-dropdown-item @click.prevent="$emit('new-attribute')">
                {{ $t('$ContactModule.AddAttribute') }}
              </vs-dropdown-item>
              <vs-dropdown-item @click.prevent="$router.push({ name: 'custom-fields' })">
                {{ $t('$ContactModule.ManageAttributes') }}
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </vx-tooltip>
      </div>
    </div>
    </div>
</template>

<script>
import VSelectServer from '@/views/modules/_components/v-select-server/VSelectServer.vue';
import { mapActions } from 'vuex';
import enums from '@/enums';

/**
 * Contact list toolbar
 *
 * @module src/views/modules/contacts/contact/ContactListToolbar
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'ContactListToolbar',
  i18n: {
    messages: {
      en: {
        ContactsMatchYourConditions: 'No contacts match your conditions | One contact matches your conditions | {count} contacts match your conditions',
        ContactsMatchToSendCampaign: 'No matching contacts to send on the campaign | One matching contact to send on the campaign | {count} contacts match to send on the campaign',
        SendCampaign: 'Send campaign',
        AllContactSelectedSelectPlaceholder: 'All selected contacts',
        CampaignSentAllContactsMsg: 'The campaign will be sent to all your contacts',
        NoContactsToSendCampaignMsg: 'No contacts to send the campaign',
      },
    },
  },
  components: {
    VSelectServer,
  },
  props: {
    selectedSegment: {
      required: true,
    },
    contactsMatchCount: {
      type: Number,
      required: true,
      default: 0,
    },
    filters: {
      type: Object,
      required: false,
    },
    filtersMatch: {
      type: String,
      required: false,
      default: enums.AppFilterMathType.ALL,
    },
    selectedFromCampaign: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      selectedSegmentLocal: this.selectedSegment,
    };
  },
  computed: {
    selectSegmentPlaceholder() {
      if (this.selectedFromCampaign) {
        return this.$t('AllContactSelectedSelectPlaceholder');
      }

      return this.$t('$ContactModule.ViewSegment');
    },
    contactsInfoMsg() {
      const msg = this.selectedFromCampaign
        ? 'ContactsMatchToSendCampaign'
        : 'ContactsMatchYourConditions';

      return this.$tc(msg,
        this.contactsMatchCount, {
          count: this.contactsMatchCount,
        });
    },
    sendToContactsMsg() {
      return this.contactsMatchCount > 0
        ? this.$t('CampaignSentAllContactsMsg')
        : this.$t('NoContactsToSendCampaignMsg');
    },
    someContactMatch() {
      return this.contactsMatchCount > 0;
    },
    someSegmentOrFilter() {
      return !!this.selectedSegmentLocal || !!this.filters;
    },
    filtersWithoutSegment() {
      return !!this.filters && !this.selectedSegmentLocal;
    },
    someContactMatchOnSegmentOrFilter() {
      return this.someContactMatch && this.someSegmentOrFilter;
    },
    selectSegmentWithContacts() {
      return !!this.selectedSegmentLocal && this.someContactMatchOnSegmentOrFilter;
    },
    selectSegmentWithoutContacts() {
      return !!this.selectedSegmentLocal && !this.someContactMatchOnSegmentOrFilter;
    },
    segmentBoxClasses() {
      if (this.selectedSegmentLocal) {
        return 'w-11/12 md:w-4/12';
      }

      return 'w-11/12 md:w-5/12';
    },
    contactsInfoBoxClasses() {
      if (this.filtersWithoutSegment) {
        if (this.someContactMatchOnSegmentOrFilter) {
          return this.selectedFromCampaign ? 'w-6/12' : 'w-4/12';
        }

        return 'w-6/12';
      }

      if (this.selectSegmentWithContacts) {
        return this.selectedFromCampaign ? 'w-7/12' : 'w-5/12';
      }

      if (this.selectSegmentWithoutContacts) {
        return 'w-7/12';
      }

      return 'w-6/12';
    },
  },
  watch: {
    selectedSegmentLocal(val) {
      this.$emit('update:selected-segment', val);
    },
    selectedSegment(val) {
      this.selectedSegmentLocal = val;
      this.updateSelectSegments();
    },
  },
  methods: {
    ...mapActions({
      fetchAllSegments: 'segment/fetchAllSegments',
    }),
    updateSelectSegments() {
      this.$refs.vSelectServer.fetchData();
    },
    async sendCampaign() {
      await this.$router.push({
        name: 'sms-campaigns-create-contacts',
        params: {
          entity: this.$enums.Entity.SMS_CAMPAIGN,
          operation: this.$enums.Operation.CREATE,
          contactsSegment: this.selectedSegment ? this.selectedSegment.id || null : null,
          contactsFilters: this.filters,
          contactsFiltersMatch: this.filtersMatch,
        },
      });
    },
  },
};
</script>

<style lang="scss">
.contact-list-toolbar {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;

  &.bg-active {
    background: rgba(var(--vs-grey-dark), 0.12)
  }

  .box:hover {
    background: rgba(var(--vs-grey-light), 0.1);
  }

  // 4-column layout
  .contact-list-toolbar-grid {
    display: grid;
    grid-template-columns: 1fr 56px;
    grid-template-areas:
      "segment more"
      "count actions";
    align-items: stretch;
    min-height: 4rem;
  }

  .ctl-col {
    display: flex;
    align-items: center;
    min-height: 3.5rem;
  }

  .ctl-col--segment { grid-area: segment; }
  .ctl-col--count { grid-area: count; padding: 0 1rem; }
  .ctl-col--actions { grid-area: actions; justify-content: center; padding: 0 0.75rem; }
  .ctl-col--more { grid-area: more; justify-content: center; padding: 0 0.75rem; }

  // Column separators (use a consistent grey separator, not theme background)
  .ctl-sep-left {
    border-left: 1px solid rgba(218, 225, 231, var(--border-opacity));
  }

  .ctl-count-text {
    line-height: 1.2;
  }

  .ctl-segment-inner {
    display: flex;
    align-items: stretch;
    width: 100%;
    min-width: 0;
    min-height: 3.5rem;
  }

  .ctl-segment-select {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    padding: 0 0.75rem;
  }

  .ctl-segment-new {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.75rem;
    white-space: nowrap;
  }

  .contact-list-toolbar-select.v-select {
    min-width: 80px;
    background-color: inherit !important;

    .vs__dropdown-toggle {
      border: none;
      border-radius: initial;
      display: flex;
      align-items: center;
      padding-top: 0;
      padding-bottom: 0;

      .vs__selected {
        color: rgba(var(--vs-content), 1);
        margin: 0;
        line-height: 1.1;
      }
    }

    .vs__selected-options {
      display: flex;
      align-items: center;
      min-width: 0;
    }

    .vs__actions {
      display: flex;
      align-items: center;
    }

    .vs__search {
      margin: 0;
      line-height: 1.1;
    }
  }
}

.viewd-segment-button {
  z-index: 10;
}

.new-segment-button {
  a {
    padding: 20px 10px;
  }
}

@media (max-width: 1100px) {
  .contact-list-toolbar {
    .options-new-view-segment {

      .view-segment-button {
        width: 50% !important;
      }

      .new-segment-button {
        width: 50% !important;
      }
    }
  }
}

@media (max-width: 800px) {
  .contact-list-toolbar {
    .options-new-view-segment {

      .view-segment-button {
        width: 55% !important;
      }

      .new-segment-button {
        width: 45% !important;
      }
    }
  }
}

@media (max-width: 767px) {
  .contact-list-toolbar {
    height: auto !important;
    min-height: 4rem;
    .contact-list-toolbar-grid {
      min-height: 4rem;
    }
  }
}

// Tablet mobile (471px–767px): keep segment + new segment side-by-side
@media (max-width: 767px) and (min-width: 471px) {
  .contact-list-toolbar {
    .ctl-segment-select {
      flex: 1 1 auto;
    }

    .ctl-segment-new {
      flex: 0 0 auto;
    }
  }
}

@media (max-width: 470px) {

  .vs-dropdown-menu {
    margin-top: -30px !important;
    margin-left: 8px !important;
  }

  .contact-list-toolbar {
    height: auto !important;
    .ctl-segment-inner {
      flex-wrap: wrap;
    }

    .ctl-segment-select {
      width: 100%;
      border-right: 0 !important;
      border-bottom: 1px solid rgba(218, 225, 231, var(--border-opacity)) !important;
      justify-content: center;

      .vs__search {
        text-align: center;
        padding-left: 10px;
      }
    }

    .ctl-segment-new {
      width: 100%;
      border-right: 0 !important;
    }
  }
}

// Desktop/tablet (md+) – keep a consistent single-row height like before
@media (min-width: 768px) {
  .contact-list-toolbar {
    height: 4rem;

    .contact-list-toolbar-grid {
      grid-template-columns: minmax(260px, 360px) 1fr auto 56px;
      grid-template-areas: "segment count actions more";
      align-items: center;
    }

    .ctl-col {
      min-height: 4rem;
    }

    // In desktop layout, count also has a left neighbour (segment), so add the separator.
    .ctl-col--count {
      border-left: 1px solid rgba(218, 225, 231, var(--border-opacity));
    }

    .ctl-col--count {
      padding: 0 1.25rem;
    }

    .ctl-col--actions {
      justify-content: center;
      min-width: 96px;
    }
  }
}

</style>
